<!--
  NotificationsList.vue (R2)

  Contenuto della campanella — header con "Segna tutte lette", lista delle
  ultime notifiche, empty state. È il pezzo CONDIVISO tra il menu desktop
  (NotificationsBell) e la dialog a tutta pagina su mobile (App.vue):
  stessa lista, due contenitori. Click su una notifica → mark-as-read +
  navigazione alla url del payload; emette `navigate` così il contenitore
  si chiude.
-->
<template>
  <div class="nlist">
    <div v-if="!hideHeader" class="nlist__header">
      <span class="nlist__title">{{ t('Notifiche') }}</span>
      <gg-btn
        v-if="notificationsStore.unreadCount > 0"
        variant="text" size="x-small"
        :text="t('Segna tutte lette')"
        @click="notificationsStore.markAllRead()"
      />
    </div>
    <v-divider v-if="!hideHeader" />
    <div v-else-if="notificationsStore.unreadCount > 0" class="nlist__toolbar">
      <gg-btn
        variant="text" size="x-small"
        :text="t('Segna tutte lette')"
        @click="notificationsStore.markAllRead()"
      />
    </div>
    <v-list
      v-if="notificationsStore.notifications.length"
      density="compact"
      :max-height="maxHeight"
      class="overflow-y-auto"
    >
      <v-list-item
        v-for="item in notificationsStore.notifications"
        :key="item.id"
        :class="{ 'nlist__item--unread': item.read_at == null }"
        @click="openNotification(item)"
      >
        <div class="nlist__item-head">
          <v-list-item-title class="nlist__item-title">
            {{ item.data.title ?? item.data.kind ?? t('Notifica') }}
          </v-list-item-title>
          <span v-if="item.created_at" class="nlist__item-date">{{ formatDate(item.created_at, 'DD MMM [·] HH:mm') }}</span>
        </div>
        <v-list-item-subtitle class="nlist__item-body">
          {{ item.data.body }}
        </v-list-item-subtitle>
        <!-- Riga azienda: cliente/prospect oggetto della notifica (icona come
             nel dettaglio rapportino) -->
        <div v-if="item.data.context?.company" class="nlist__item-company">
          <v-icon icon="ms:domain" size="13" class="mr-1" />{{ item.data.context.company }}
        </div>
        <!-- Riga soggetto: di chi è l'evento, solo se la notifica non riguarda me
             (come la card rapportini mobile: icona utente + nome) -->
        <div v-if="subjectLabel(item)" class="nlist__item-subject">
          <v-icon icon="ms:person" size="13" class="mr-1" />{{ subjectLabel(item) }}
        </div>
      </v-list-item>
    </v-list>
    <div v-else class="nlist__empty">{{ t('Nessuna notifica') }}</div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '@/stores/useNotificationsStore'
import { useAuthStore } from '@/stores/auth'
import { useDate } from '@/composables/useDate'
import type { AppNotification } from '@/models/commesse'

withDefaults(defineProps<{
  /** Il contenitore ha già un titolo (es. GgDialog): niente header interno */
  hideHeader?: boolean
  /** Altezza massima della lista (menu desktop); undefined = tutta la dialog */
  maxHeight?: number | string
}>(), { hideHeader: false, maxHeight: undefined })

const emit = defineEmits<{
  navigate: [item: AppNotification]
}>()

const { t } = useI18n()
const router = useRouter()
const notificationsStore = useNotificationsStore()
const authStore = useAuthStore()
const { formatDate } = useDate()

/**
 * Nome/i del soggetto dell'evento, o stringa vuota se la notifica riguarda
 * il destinatario stesso (unico soggetto = io) o non ha soggetti (eventi
 * di sistema, notifiche pre-motore).
 */
function subjectLabel(item: AppNotification): string {
  const subjects = item.data.subjects ?? []
  if (!subjects.length) return ''
  const meId = authStore.user?.id
  if (subjects.length === 1 && meId != null && subjects[0].id === Number(meId)) return ''
  return subjects.map((s) => s.name).filter(Boolean).join(', ')
}

function openNotification(item: AppNotification): void {
  if (item.read_at == null) notificationsStore.markRead(item.id).catch(() => {})
  emit('navigate', item)
  if (item.data.url) router.push(item.data.url)
}

onMounted(() => notificationsStore.fetchNotifications().catch(() => {}))
</script>

<style scoped lang="scss">
.nlist {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
  }

  &__toolbar {
    display: flex;
    justify-content: flex-end;
    padding: 4px 8px 0;
  }

  &__title { font-size: 14px; font-weight: 700; }

  &__empty {
    padding: 24px 16px;
    font-size: 13px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    text-align: center;
  }

  &__item--unread {
    background: rgba(var(--v-theme-primary), 0.05);

    .nlist__item-title { font-weight: 700; }
  }

  &__item-title { font-size: 13px; }
  &__item-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;

    .nlist__item-title { min-width: 0; }
    .nlist__item-date { flex-shrink: 0; white-space: nowrap; }
  }

  // Corpo su massimo 2 righe (niente tooltip: il testo cresce come su mobile)
  &__item-body {
    font-size: 12px;
    white-space: normal;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2;
    overflow: hidden;
  }
  &__item-company {
    display: flex;
    align-items: center;
    font-size: 0.7rem;
    margin-top: 2px;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }
  &__item-subject {
    display: flex;
    align-items: center;
    font-size: 0.7rem;
    margin-top: 2px;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }
  &__item-date { font-size: 10px; color: rgba(var(--v-theme-on-surface), 0.45); }
}
</style>
